import { useContext, useMemo, useState } from "react";
import { Localization, SessionContext, SettingsManager } from "@viamap/viamap2-common";
import { InfoMessageModal } from "src/components/AppMessagesComponents";
import { ManageDocuments } from "src/components/ManageDocuments";
import { GlassCheckbox } from "src/components/MitGlassComponents";
import { ManagedWindow } from "src/WindowManager/WindowState";
import { getPLAHierarchicalRelations, getMyPLAAccessRights, hasPLAWriteAccess, PLAElementRecord, PLAProject, PLARelationRecord } from "./PLAState";

function plaCreateDocumentPrefix(path: string, projectId: string, elementId: string): string {
   if (elementId) return `${path}${projectId}/${elementId}/`;
   return `${path}${projectId}/`;
}

function getPLAParentElementIds(project: PLAProject, elementId: string, relations: PLARelationRecord[]) {
   const hierarchicalRelations = getPLAHierarchicalRelations(project, relations);
   const parentIds: string[] = [];
   let currentElementId = elementId;
   const visitedElementIds = new Set<string>([elementId]);

   while (currentElementId) {
      const parentRelation = hierarchicalRelations.find((relation) => relation.toId === currentElementId && !visitedElementIds.has(relation.fromId));
      if (!parentRelation) break;
      parentIds.push(parentRelation.fromId);
      visitedElementIds.add(parentRelation.fromId);
      currentElementId = parentRelation.fromId;
   }

   return parentIds;
}

export function PLADocuments(props: {
   managedWindow: ManagedWindow;
   callbackOnClose: () => void;
}) {
   const { state: sessionState } = useContext(SessionContext);
   const [includeParentDocuments, setIncludeParentDocuments] = useState(false);
   const [includeAllDocuments, setIncludeAllDocuments] = useState(false);
   const project = props.managedWindow.windowParams?.project as PLAProject | undefined;
   const element = props.managedWindow.windowParams?.element as PLAElementRecord | undefined;
   const elements = (props.managedWindow.windowParams?.elements || []) as PLAElementRecord[];
   const relations = (props.managedWindow.windowParams?.relations || []) as PLARelationRecord[];
   const parentElementIds = useMemo(() => project && element ? getPLAParentElementIds(project, element.id, relations) : [], [project, element, relations]);

   if (!project || !element) {
      return <InfoMessageModal message={Localization.getText("Please select exactly one element to manage documents.")} onDismiss={props.callbackOnClose} />;
   }

   const bucket = SettingsManager.getSystemSetting("pla.documentStorageS3Bucket");
   const path = SettingsManager.getSystemSetting("pla.documentStorageS3Path");
   const projectId = project.id || "unknown_project";
   const elementId = element.id;
   const isProjectLevel = elementId === "";
   const additionalPaths = includeParentDocuments && !isProjectLevel
      ? [
         { path: plaCreateDocumentPrefix(path, projectId, "") },
         ...parentElementIds.map((parentElementId) => ({ path: plaCreateDocumentPrefix(path, projectId, parentElementId) }))
      ]
      : [];
   const hasProjectAccess = getMyPLAAccessRights(project, sessionState.userRef, sessionState.customerRef).length > 0;
   const hasWriteAccess = hasPLAWriteAccess(project, sessionState.userRef, sessionState.customerRef);

   return (
      <ManageDocuments
         managedWindow={props.managedWindow}
         callbackOnClose={props.callbackOnClose}
         projectName={project.name || "Unknown Project"}
         projectId={project.id}
         bucket={bucket}
         path={plaCreateDocumentPrefix(path, projectId, elementId)}
         additionalPaths={additionalPaths}
         filterProjectRootOnly={(!includeAllDocuments && isProjectLevel) || (includeParentDocuments && !isProjectLevel)}
         toolbarContent={isProjectLevel ? (
            <GlassCheckbox checked={includeAllDocuments} onClick={(_event, value) => setIncludeAllDocuments(value)}>
               {Localization.getText("Get all documents")}
            </GlassCheckbox>
         ) : (
            <GlassCheckbox checked={includeParentDocuments} onClick={(_event, value) => setIncludeParentDocuments(value)}>
               {Localization.getText("Show inherited documents")}
            </GlassCheckbox>
         )}
         resolveElementName={(itemElementId: string) => elements.find((item) => item.id === itemElementId)?.name || itemElementId}
         uploadAvailable={hasWriteAccess}
         deleteAvailable={hasWriteAccess}
         openAvailable={hasProjectAccess}
         downloadAvailable={hasProjectAccess}
         copyLinkAvailable={false}
         createUniqueFileName={false}
         linksAvailable={true}
      />
   );
}